import {
  FileKind,
  ResultItem,
  ResultKey,
  RESULT_META,
  RESULT_ORDER,
  TranscriptionFile,
  WorkRecord,
  availableResults,
  escapeHtml,
} from "./core";

export const API_RESULT_BY_KEY: Record<ResultKey, AiTranscription.ResultType> = {
  original: "ORIGINAL",
  visual: "VISUAL",
  summary: "SUMMARY",
  structure: "STRUCTURE",
  rewrite: "REWRITE",
};

export const RESULT_KEY_BY_API: Record<AiTranscription.ResultType, ResultKey> = {
  ORIGINAL: "original",
  VISUAL: "visual",
  SUMMARY: "summary",
  STRUCTURE: "structure",
  REWRITE: "rewrite",
};

const BREAKDOWN_LABELS: Record<string, string> = {
  documentOriginal: "文档提取",
  original: "提取原文",
  videoAnalysis: "视频理解（含画面概述）",
  summary: "总结",
  structure: "文案结构拆解",
  rewrite: "重写",
};

export function toApiResultTypes(keys: ResultKey[]): AiTranscription.ResultType[] {
  return keys.map((key) => API_RESULT_BY_KEY[key]);
}

export function fromApiResultType(type: string | undefined): ResultKey | null {
  if (!type) return null;
  return RESULT_KEY_BY_API[type as AiTranscription.ResultType] || null;
}

export function fileKindFromAssetType(assetType?: string | null): FileKind {
  if (assetType === "VIDEO") return "video";
  if (assetType === "AUDIO") return "audio";
  return "doc";
}

export function isRunningStatus(status?: string | null): boolean {
  return status === "QUEUED" || status === "PROCESSING";
}

export function isTerminalStatus(status?: string | null): boolean {
  return status === "SUCCESS" || status === "PARTIAL_SUCCESS" || status === "FAILED";
}

export function apiErrorMessage(error: unknown, fallback = "请求失败，请重试"): string {
  const err = error as { msg?: string; message?: string; response?: { data?: { msg?: string } } };
  return (
    (typeof err?.msg === "string" && err.msg.trim()) ||
    (typeof err?.response?.data?.msg === "string" && err.response.data.msg.trim()) ||
    (typeof err?.message === "string" && err.message.trim()) ||
    fallback
  );
}

export function selectionQuoteKey(keys: ResultKey[]): string {
  return [...toApiResultTypes(keys)].sort().join("|");
}

export function quoteFromApi(quote?: AiTranscription.Quote | null): {
  total: number;
  lines: Array<{ key: string; label: string; amount: number }>;
} {
  if (!quote) return { total: 0, lines: [] };
  const total = Math.max(0, Number(quote.totalPoint || 0));
  const lines = Object.entries(quote.breakdown || {}).map(([key, amount]) => ({
    key,
    label: BREAKDOWN_LABELS[key] || RESULT_META[fromApiResultType(key) || "summary"]?.[0] || key,
    amount: Number(amount) || 0,
  }));
  return { total, lines };
}

export function pickQuote(
  preview: AiTranscription.Quote | null | undefined,
  keys: ResultKey[],
): AiTranscription.Quote | null {
  if (!preview) return null;
  if (preview.selectionQuotes) {
    return preview.selectionQuotes[selectionQuoteKey(keys)] || null;
  }
  return preview;
}

export function fileFromTask(task: {
  assetType?: string;
  filename?: string;
  title?: string;
  fileSize?: number;
  durationMillis?: number;
  pageCount?: number;
  characterCount?: number;
}): TranscriptionFile {
  const type = fileKindFromAssetType(task.assetType);
  return {
    name: task.filename || task.title || "未命名文件",
    type,
    seconds: Number(task.durationMillis || 0) / 1000,
    bytes: Number(task.fileSize || 0),
    chars: task.characterCount,
    pages: task.pageCount,
    sample: false,
  };
}

export function resultHtmlFromPlain(plain: string, key: ResultKey): string {
  const text = String(plain || "").replace(/\r\n?/g, "\n").trim();
  if (!text) return "";
  if (key === "summary") {
    const sections: Array<{ title: string; paragraphs: string[]; items: string[] }> = [];
    let section: { title: string; paragraphs: string[]; items: string[] } | null = null;
    let paragraphLines: string[] = [];
    const flushParagraph = () => {
      if (section && paragraphLines.length) {
        section.paragraphs.push(paragraphLines.join("\n"));
        paragraphLines = [];
      }
    };
    text.split("\n").forEach((raw) => {
      const line = raw.trim();
      if (!line) {
        flushParagraph();
        return;
      }
      const heading = line.match(/^(?:[#\s]*)(内容摘要|核心要点)\s*[:：]?\s*(.*)$/);
      if (heading) {
        flushParagraph();
        section = { title: heading[1], paragraphs: [], items: [] };
        sections.push(section);
        if (heading[2]) paragraphLines.push(heading[2]);
        return;
      }
      if (!section) {
        section = { title: "", paragraphs: [], items: [] };
        sections.push(section);
      }
      if (section.title === "核心要点") {
        flushParagraph();
        section.items.push(line.replace(/^(?:[•·▪◦]|[-*]|\d+[.、)])\s*/, ""));
        return;
      }
      paragraphLines.push(line);
    });
    flushParagraph();
    if (sections.some((item) => item.title)) {
      return sections
        .map((item) => {
          const heading = item.title && item.title !== "内容摘要"
            ? `<h3>${escapeHtml(item.title)}</h3>`
            : "";
          const paragraphs = item.paragraphs.map((p) => `<p>${escapeHtml(p)}</p>`).join("");
          const list = item.items.length
            ? `<ul>${item.items.map((li) => `<li>${escapeHtml(li)}</li>`).join("")}</ul>`
            : "";
          return `${heading}${paragraphs}${list}`;
        })
        .join("");
    }
  }
  return text
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join("");
}

function mapResultItem(item: AiTranscription.ResultItem): ResultItem {
  const status = String(item.status || "").toUpperCase();
  const mapped: ResultItem = {
    status:
      status === "SUCCESS"
        ? "success"
        : status === "FAILED"
          ? "failed"
          : status === "SKIPPED"
            ? "skipped"
            : "pending",
    plain: item.plainText || "",
    basis: item.basis || undefined,
    structureRows: Array.isArray(item.structureRows)
      ? item.structureRows.map((row) => ({
          segment: row.segment || "",
          location: row.location || "",
          mainContent: row.mainContent || "",
          expressionRole: row.expressionRole || "",
        }))
      : undefined,
    prompt: item.rewriteRequirement || undefined,
    reason: item.errorMessage || undefined,
  };
  const key = fromApiResultType(item.resultType);
  if (mapped.status === "success" && key) {
    mapped.html = resultHtmlFromPlain(mapped.plain || "", key);
  }
  return mapped;
}

function mapResults(list?: AiTranscription.ResultItem[]): WorkRecord["results"] {
  const results: WorkRecord["results"] = {};
  (list || []).forEach((item) => {
    const key = fromApiResultType(item.resultType);
    if (key) results[key] = mapResultItem(item);
  });
  return results;
}

function selectedKeys(task: {
  selectedResultTypes?: AiTranscription.ResultType[];
  results?: AiTranscription.ResultItem[];
  assetType?: string;
}): ResultKey[] {
  const fromSelected = (task.selectedResultTypes || [])
    .map((type) => fromApiResultType(type))
    .filter((key): key is ResultKey => !!key);
  if (fromSelected.length) return fromSelected;
  const fromResults = (task.results || [])
    .map((item) => fromApiResultType(item.resultType))
    .filter((key): key is ResultKey => !!key);
  if (fromResults.length) return fromResults;
  return availableResults(fileFromTask(task));
}

function firstActive(results: WorkRecord["results"], keys: ResultKey[]): ResultKey {
  return (
    RESULT_ORDER.find((key) => keys.includes(key) && results[key]?.status === "success") ||
    RESULT_ORDER.find((key) => results[key]?.status === "success") ||
    keys[0] ||
    "summary"
  );
}

export function taskToWork(task: AiTranscription.Task): WorkRecord {
  const file = fileFromTask(task);
  const selected = selectedKeys(task);
  const results = mapResults(task.results);
  const running = isRunningStatus(task.status);
  const warnings = (task.results || [])
    .filter((item) => ["FAILED", "SKIPPED"].includes(String(item.status || "").toUpperCase()))
    .map((item) => {
      const key = fromApiResultType(item.resultType);
      const label = key ? (key === "original" ? "提取原文" : RESULT_META[key][0]) : item.resultType;
      return `${label}：${item.errorMessage || "未生成"}`;
    });
  return {
    workId: String(task.id),
    createdAt: Date.parse(String(task.createTime || "")) || Date.now(),
    file,
    selected,
    prompt: task.rewriteRequirement || "",
    cache: {},
    results,
    active: firstActive(results, selected),
    job: running
      ? {
          id: task.id,
          keys: selected,
          steps: [],
          index: 0,
          due: 0,
          estimate: 0,
          scenario: "normal",
          base: {},
          failed: [],
          paid: true,
        }
      : null,
    charged: Number(task.totalConsumedPoint || 0),
    lastCharge: Number(task.totalConsumedPoint || 0),
    warnings,
    lastKeys: selected,
    scenario: "normal",
    taskStatus: task.status,
    progress: Number(task.progress || 0),
    currentStep: task.currentStep || undefined,
    totalConsumedPoint: Number(task.totalConsumedPoint || 0),
    generatedResultCount: Number.isFinite(Number(task.generatedResultCount))
      ? Math.max(0, Number(task.generatedResultCount))
      : undefined,
  };
}

export function templateToWork(template: AiTranscription.Template): WorkRecord {
  const file = fileFromTask({
    assetType: template.assetType,
    filename: template.filename || template.name,
    fileSize: template.fileSize,
    durationMillis: template.durationMillis,
    pageCount: template.pageCount,
    characterCount: template.characterCount,
  });
  const selected = (template.defaultResultTypes || [])
    .map((type) => fromApiResultType(type))
    .filter((key): key is ResultKey => !!key);
  const keys = selected.length ? selected : availableResults(file);
  const results = mapResults(template.results);
  if (!Object.keys(results).length) {
    keys.forEach((key) => {
      results[key] = { status: "success", plain: "", html: "" };
    });
  }
  return {
    workId: `template-${template.id}`,
    createdAt: Date.now(),
    isTemplate: true,
    templateKey: file.type,
    templateId: template.id,
    file,
    selected: keys,
    prompt: template.defaultRewriteRequirement || "",
    cache: {},
    results,
    active: firstActive(results, keys),
    job: null,
    charged: 0,
    warnings: [],
    lastKeys: keys,
    scenario: "normal",
    taskStatus: "SUCCESS",
    progress: 100,
  };
}

export function mergeProgress(previous: number | undefined, next: number | undefined): number {
  const current = Math.max(0, Math.min(100, Number(next || 0)));
  const last = Math.max(0, Number(previous || 0));
  return Math.max(last, current);
}

export function compactDuration(milliseconds?: number | null): string {
  if (!Number.isFinite(Number(milliseconds)) || Number(milliseconds) <= 0) return "";
  const seconds = Math.round(Number(milliseconds) / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export function applyTaskMetadata(
  file: TranscriptionFile,
  metadata?: AiTranscription.FileMetadata | null,
): TranscriptionFile {
  if (!metadata) return file;
  return {
    ...file,
    type: metadata.assetType ? fileKindFromAssetType(metadata.assetType) : file.type,
    bytes: metadata.fileSize ?? file.bytes,
    seconds: metadata.durationMillis != null ? Number(metadata.durationMillis) / 1000 : file.seconds,
    pages: metadata.pageCount ?? file.pages,
    chars: metadata.characterCount ?? file.chars,
  };
}
